import React from 'react';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages, onPageChange }) => {

    const handlePageChange = (page: number) => {
        if (page !== currentPage && page >= 1 && page <= totalPages) {
            onPageChange(page);
        }
    };

    // Condensed/ellipsized pagination logic
    const renderPageNumbers = () => {
        const pageNumbers = [];
        const maxPagesToShow = 7;
        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(
                    <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}
                        style={{margin: 0}}
                    >
                        <button
                            className="page-link"
                            style={{
                                border: currentPage === i ? '1.5px solid #1976d2' : 'none',
                                background: currentPage === i ? '#e3f2fd' : 'none',
                                color: currentPage === i ? '#1976d2' : '#333',
                                padding: '0.5rem 0.75rem',
                                borderRadius: '4px',
                                fontWeight: 500,
                                cursor: currentPage === i ? 'default' : 'pointer',
                                opacity: currentPage === i ? 1 : 0.85,
                            }}
                            onClick={() => handlePageChange(i)}
                            disabled={currentPage === i}
                        >
                            {i}
                        </button>
                    </li>
                );
            }
        } else {
            // Always show first, last, current, and neighbors
            const left = Math.max(2, currentPage - 1);
            const right = Math.min(totalPages - 1, currentPage + 1);

            // First page
            pageNumbers.push(
                <li key={1} className={`page-item ${currentPage === 1 ? 'active' : ''}`}
                    style={{margin: 0}}
                >
                    <button
                        className="page-link"
                        style={{
                            border: currentPage === 1 ? '1.5px solid #1976d2' : 'none',
                            background: currentPage === 1 ? '#e3f2fd' : 'none',
                            color: currentPage === 1 ? '#1976d2' : '#333',
                            padding: '0.5rem 0.75rem',
                            borderRadius: '4px',
                            fontWeight: 500,
                            cursor: currentPage === 1 ? 'default' : 'pointer',
                            opacity: currentPage === 1 ? 1 : 0.85,
                        }}
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                    >
                        1
                    </button>
                </li>
            );

            // Ellipsis if needed
            if (left > 2) {
                pageNumbers.push(
                    <li key="start-ellipsis" className="page-item disabled" style={{margin: 0}}>
                        <span className="page-link" style={{background: 'none', border: 'none', color: '#888', padding: '0.5rem 0.75rem'}}>&hellip;</span>
                    </li>
                );
            }

            // Middle page numbers
            for (let i = left; i <= right; i++) {
                pageNumbers.push(
                    <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}
                        style={{margin: 0}}
                    >
                        <button
                            className="page-link"
                            style={{
                                border: currentPage === i ? '1.5px solid #1976d2' : 'none',
                                background: currentPage === i ? '#e3f2fd' : 'none',
                                color: currentPage === i ? '#1976d2' : '#333',
                                padding: '0.5rem 0.75rem',
                                borderRadius: '4px',
                                fontWeight: 500,
                                cursor: currentPage === i ? 'default' : 'pointer',
                                opacity: currentPage === i ? 1 : 0.85,
                            }}
                            onClick={() => handlePageChange(i)}
                            disabled={currentPage === i}
                        >
                            {i}
                        </button>
                    </li>
                );
            }

            // Ellipsis if needed
            if (right < totalPages - 1) {
                pageNumbers.push(
                    <li key="end-ellipsis" className="page-item disabled" style={{margin: 0}}>
                        <span className="page-link" style={{background: 'none', border: 'none', color: '#888', padding: '0.5rem 0.75rem'}}>&hellip;</span>
                    </li>
                );
            }

            // Last page
            pageNumbers.push(
                <li key={totalPages} className={`page-item ${currentPage === totalPages ? 'active' : ''}`}
                    style={{margin: 0}}
                >
                    <button
                        className="page-link"
                        style={{
                            border: currentPage === totalPages ? '1.5px solid #1976d2' : 'none',
                            background: currentPage === totalPages ? '#e3f2fd' : 'none',
                            color: currentPage === totalPages ? '#1976d2' : '#333',
                            padding: '0.5rem 0.75rem',
                            borderRadius: '4px',
                            fontWeight: 500,
                            cursor: currentPage === totalPages ? 'default' : 'pointer',
                            opacity: currentPage === totalPages ? 1 : 0.85,
                        }}
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                    >
                        {totalPages}
                    </button>
                </li>
            );
        }
        return pageNumbers;
    };

    return (
        <nav aria-label="Page navigation" style={{ display: 'flex', justifyContent: 'center', margin: '2rem 0' }}>
            <ul
                className="pagination"
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '0.25rem',
                    padding: 0,
                    listStyle: 'none',
                    background: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                }}
            >
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`} style={{margin: 0}}>
                    <button
                        className="page-link"
                        style={{
                            border: 'none',
                            background: 'none',
                            color: '#1976d2',
                            padding: '0.5rem 0.75rem',
                            borderRadius: '4px',
                            fontWeight: 500,
                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                            opacity: currentPage === 1 ? 0.5 : 1,
                        }}
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        Previous
                    </button>
                </li>
                {renderPageNumbers()}
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`} style={{margin: 0}}>
                    <button
                        className="page-link"
                        style={{
                            border: 'none',
                            background: 'none',
                            color: '#1976d2',
                            padding: '0.5rem 0.75rem',
                            borderRadius: '4px',
                            fontWeight: 500,
                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                            opacity: currentPage === totalPages ? 0.5 : 1,
                        }}
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        Next
                    </button>
                </li>
            </ul>
        </nav>
    );
};

export default Pagination;
